import { useState } from "react";
import { ChevronDown, ChevronRight, Zap, Users } from "lucide-react";
import { ObservationCard } from "@/components/ObservationCard";
import { HAZARD_BASE } from "@/lib/hazard";
import { IncidentBadges } from "@/components/IncidentBadges";

const fmtPeople = (n) => (n >= 1e6 ? `${(n / 1e6).toFixed(1)}M` : n >= 1e3 ? `${Math.round(n / 1e3)}k` : `${n}`);
const tone = (u) => (u >= 75 ? "bg-atlas-red text-white" : u >= 55 ? "bg-atlas-orange text-atlas-void" : "bg-white/15 text-white");

// One real-world situation: urgency, scope, why it ranks — expands to its individual readings.
export const IncidentCard = ({ inc, rank, hypByObs, onSelect, onFocus, activeUid }) => {
  const [open, setOpen] = useState(false);
  const lead = inc.lead;
  const single = inc.members.length === 1;
  if (single) return <ObservationCard obs={lead} topHyp={hypByObs[lead.uid]} onSelect={onSelect} active={activeUid === lead.uid} rank={rank} urgency={inc.urgency} ahead={inc.ahead} extra={<IncidentBadges inc={inc} />} />;
  return (
    <div className="border-b border-white/10" data-testid={`incident-card-${inc.id}`}>
      <div className="flex gap-3 p-3 cursor-pointer hover:bg-white/[0.03] transition-colors" onClick={() => onFocus(inc)}>
        <div className="flex flex-col items-center gap-1 shrink-0 w-10">
          <span className="text-[9px] font-mono text-atlas-secondary">#{rank}</span>
          <span className={`w-10 h-7 flex items-center justify-center text-sm font-mono font-bold ${tone(inc.urgency)}`}
            title="Urgency 0–100: severity past threshold, people exposed, growth, breadth, and whether an agency has registered an event yet"
            data-testid={`incident-urgency-${inc.id}`}>{inc.urgency}</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-0.5">
            {inc.ahead && <span className="flex items-center gap-1 text-[9px] font-mono uppercase tracking-widest text-atlas-cyan border border-atlas-cyan/40 px-1" data-testid={`incident-ahead-${inc.id}`}><Zap className="w-2.5 h-2.5" /> No agency alert yet</span>}
            {inc.warning && <span className="text-[9px] font-mono uppercase tracking-widest text-atlas-red border border-atlas-red/40 px-1">Warning</span>}
          </div>
          <h3 className="text-sm font-display text-white leading-snug truncate" data-testid={`incident-title-${inc.id}`}>{inc.title}</h3>
          <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono text-atlas-secondary mt-0.5">
            {Object.entries(inc.byHazard).map(([h, n]) => (
              <span key={h} className="flex items-center gap-1"><span className="w-1.5 h-1.5 rounded-full" style={{ background: HAZARD_BASE[h] }} />{n} {h}</span>
            ))}
            {inc.people > 0 && <span className="flex items-center gap-1"><Users className="w-3 h-3" />{fmtPeople(inc.people)} exposed</span>}
          </div>
          <IncidentBadges inc={inc} />
          <p className="text-[11px] text-atlas-secondary mt-1 line-clamp-2">
            Strongest: {lead.region} — {lead.forecast?.headline || lead.explanation_note || `${lead.value} ${lead.unit}`}
          </p>
        </div>
        <button onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }} data-testid={`incident-expand-${inc.id}`}
          className="self-start flex items-center gap-1 h-6 px-1.5 text-[9px] font-mono uppercase tracking-wider text-atlas-secondary border border-white/10 hover:text-white hover:border-white/30 transition-colors">
          {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}{inc.members.length} sites
        </button>
      </div>
      {open && (
        <div className="pl-6 bg-black/20 border-t border-white/5" data-testid={`incident-members-${inc.id}`}>
          {inc.members.map((o) => (
            <ObservationCard key={o.uid} obs={o} topHyp={hypByObs[o.uid]} onSelect={onSelect} active={activeUid === o.uid} />
          ))}
        </div>
      )}
    </div>
  );
};
